import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiBookOpen, FiCalendar, FiClock, FiZap, FiAlertCircle } from 'react-icons/fi';
import { getChicagoNow, parseUTCToChicago, createChicagoDateTime } from '../../utils/datetime-comprehensive';
import apiService from '../../services/api';
import RichTextEditorTipTap from '../common/RichTextEditor';
import { sanitizeHtmlForViewer } from '../../lib/richtext/htmlSanitizer';
import logger from '../../utils/logger';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 4rem 1rem 2rem 1rem;
  min-height: 100vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 3rem 0.5rem 1rem 0.5rem;
    align-items: flex-start;
  }
`;

const ModalContent = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 700px;
  max-height: 85vh;
  overflow-y: auto;
  position: relative;
  margin: 1rem 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 95vh;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-primary) 100%);
  padding: 1.75rem 1.5rem 1.5rem;
  border-bottom: 1px solid rgba(216, 184, 137, 0.1);
  position: relative;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem 1rem;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  letter-spacing: -0.025em;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);
  color: var(--text-secondary);

  &:hover {
    background: var(--background-secondary);
    border-color: rgba(216, 184, 137, 0.3);
    color: var(--text-primary);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 2.25rem;
    height: 2.25rem;
    top: 0.75rem;
    right: 0.75rem;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 1.25rem;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Required = styled.span`
  color: var(--error-color);
  font-weight: 600;
`;

const Input = styled.input`
  padding: 1rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  background: var(--background-secondary);
  font-weight: 500;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background-color: var(--background-primary);
    box-shadow: 0 0 0 4px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }

  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 0.9375rem;
  }
`;

const Select = styled.select`
  padding: 1rem 2.5rem 1rem 1rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  background: var(--background-secondary);
  font-weight: 500;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background-color: var(--background-primary);
    box-shadow: 0 0 0 4px var(--focus-ring);
  }

  @media (max-width: 768px) {
    padding: 0.875rem 2.25rem 0.875rem 0.875rem;
    font-size: 0.9375rem;
    background-position: right 0.625rem center;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-tertiary) 100%);
  border: 1px solid rgba(216, 184, 137, 0.15);
  border-radius: 0;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: var(--primary-color);
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  cursor: pointer;
  line-height: 1.4;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(216, 184, 137, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1.25rem;
    padding-top: 1.25rem;
  }
`;

const Button = styled.button`
  padding: 0.875rem 2rem;
  border-radius: 0;
  font-weight: 600;
  font-size: 1rem;
  min-width: 120px;
  letter-spacing: 0.025em;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  cursor: pointer;
  border: none;
  position: relative;
  overflow: hidden;

  &.primary {
    background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
    color: var(--text-inverse);
    box-shadow: 0 4px 12px rgba(216, 184, 137, 0.3);

    &:hover {
      background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-color) 100%);
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(216, 184, 137, 0.4);
    }

    &:active {
      transform: translateY(0);
    }

    &:disabled {
      background: var(--background-tertiary);
      color: var(--text-muted);
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }

  &.secondary {
    background: var(--background-secondary);
    color: var(--text-secondary);
    border: 2px solid var(--border-color);

    &:hover {
      background: var(--background-tertiary);
      color: var(--text-primary);
      border-color: var(--border-dark);
    }
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9375rem;
    min-width: auto;
    width: 100%;
  }
`;

const CourseInfo = styled.div`
  background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-tertiary) 100%);
  border: 1px solid rgba(216, 184, 137, 0.1);
  border-radius: 0;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: var(--shadow-sm);
`;

const CourseTitle = styled.h3`
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.25rem 0;
  font-size: 1rem;
  letter-spacing: -0.025em;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const CourseCode = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const DateTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DateTimeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DateTimeInput = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DateTimeLabel = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DateTimeInputField = styled.input`
  padding: 1rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  background: var(--background-secondary);
  font-weight: 500;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  position: relative;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background-color: var(--background-primary);
    box-shadow: 0 0 0 4px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }

  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 0.9375rem;
  }
`;

const PresetButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const PresetButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--background-secondary);
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background: var(--background-tertiary);
    color: var(--text-primary);
    border-color: var(--border-dark);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ClearButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--error-color);
  border-radius: 0;
  background: rgba(252, 129, 129, 0.1);
  color: var(--error-color);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  &:hover {
    background: rgba(252, 129, 129, 0.15);
    color: var(--error-dark);
    border-color: var(--error-color);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const TimezoneInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-tertiary) 100%);
  border: 1px solid rgba(216, 184, 137, 0.15);
  border-radius: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 2rem;
  color: var(--text-secondary);
  font-size: 1rem;
  font-weight: 500;
`;

const CurrentDescriptionLabel = styled.h3`
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  margin: 1.5rem 0 0.75rem 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin: 1rem 0 0.5rem 0;
  }
`;

const CurrentDescription = styled.div`
  color: var(--text-secondary);
  margin-top: 0;
  line-height: 1.6;
  font-size: 0.875rem;
  background: rgba(216, 184, 137, 0.03);
  border: 1px solid rgba(216, 184, 137, 0.1);
  border-radius: 0;
  padding: 1.25rem;
  font-family: 'Noto Sans Arabic', 'Amiri', 'Scheherazade New', 'Inter', 'Arial Unicode MS', 'Tahoma', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 0;
  }

  /* Rich text content styling */
  p {
    margin: 0 0 1rem 0;
    line-height: 1.6;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 1rem 0 0.5rem 0;
    color: var(--text-primary);
    font-weight: 600;
    line-height: 1.3;
    
    &:first-child {
      margin-top: 0;
    }
  }

  h1 { font-size: 1.25rem; }
  h2 { font-size: 1.125rem; }
  h3 { font-size: 1rem; }
  h4 { font-size: 0.95rem; }
  h5 { font-size: 0.9rem; }
  h6 { font-size: 0.875rem; }

  ul, ol {
    margin: 0.75rem 0;
    padding-left: 1.5rem;
    
    li {
      margin: 0.25rem 0;
      line-height: 1.5;
    }
  }

  blockquote {
    margin: 1rem 0;
    padding: 0.75rem 1rem;
    border-left: 4px solid var(--primary-color);
    background: var(--background-secondary);
    border-radius: 0;
    font-style: italic;
  }

  code {
    background: var(--background-secondary);
    padding: 0.125rem 0.375rem;
    border-radius: 0;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.875em;
    color: var(--text-primary);
  }

  pre {
    background: var(--background-secondary);
    padding: 1rem;
    border-radius: 0;
    overflow-x: auto;
    margin: 1rem 0;
    
    code {
      background: none;
      padding: 0;
      border-radius: 0;
    }
  }

  a {
    color: var(--primary-color);
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: all 0.2s ease;
    
    &:hover {
      color: var(--primary-hover);
      border-bottom-color: var(--primary-color);
      text-decoration: none;
    }
  }

  strong, b {
    font-weight: 600;
    color: var(--text-primary);
  }

  em, i {
    font-style: italic;
  }

  u {
    text-decoration: underline;
  }
`;


function EditAssignmentModal({ isOpen, onClose, assignment, onAssignmentUpdated }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: { content_json: null, content_html: '', content_plain: '' },
    points: 100,
    dueDate: '',
    dueTime: '',
    type: 'other',
    allowResubmission: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [assignmentData, setAssignmentData] = useState(null);

  const fetchAssignmentDetails = useCallback(async () => {
    if (!assignment?.id) {
      logger.warn('No assignment ID provided');
      return;
    }
    
    try {
      setDataLoading(true);
      setErrors({});
      logger.debug('Fetching assignment details', { assignmentId: assignment.id });

      const response = await apiService.getAssignment(assignment.id);
      logger.debug('Assignment API response', { success: response.success, hasAssignment: !!response.assignment });

      if (response.success && response.assignment) {
        const data = response.assignment;
        setAssignmentData(data);
        
        // Parse due date
        const chicagoDateTime = data.due_date ? parseUTCToChicago(data.due_date) : null;
        
        // Update form data with fetched assignment details
        const newFormData = {
          title: data.title || '',
          description: data.description 
            ? { content_json: null, content_html: data.description, content_plain: data.description.replace(/<[^>]*>/g, '') }
            : { content_json: null, content_html: '', content_plain: '' },
          points: data.points || 100,
          dueDate: chicagoDateTime ? chicagoDateTime.toFormat('yyyy-MM-dd') : '',
          dueTime: chicagoDateTime ? chicagoDateTime.toFormat('HH:mm') : '',
          type: data.assignment_type || 'other',
          allowResubmission: data.allow_resubmission || false
        };
        
        setFormData(newFormData);
        setErrors({});
        
        logger.debug('Form data updated with assignment details', {
          title: newFormData.title,
          points: newFormData.points,
          dueDate: newFormData.dueDate || 'none',
          dueTime: newFormData.dueTime || 'none',
          type: newFormData.type,
          allowResubmission: newFormData.allowResubmission,
          descriptionLength: newFormData.description.html?.length || 0
        });
      } else {
        logger.error('Failed to fetch assignment details', { response, assignmentId: assignment?.id });
        // Fallback to using the assignment prop data
        if (assignment) {
          logger.debug('Falling back to assignment prop data', { assignmentId: assignment.id });
          const chicagoDateTime = assignment.due_date ? parseUTCToChicago(assignment.due_date) : null;
          
          const fallbackFormData = {
            title: assignment.title || '',
            description: assignment.description 
              ? { content_json: null, content_html: assignment.description, content_plain: assignment.description.replace(/<[^>]*>/g, '') }
              : { content_json: null, content_html: '', content_plain: '' },
            points: assignment.points || 100,
            dueDate: chicagoDateTime ? chicagoDateTime.toFormat('yyyy-MM-dd') : '',
            dueTime: chicagoDateTime ? chicagoDateTime.toFormat('HH:mm') : '',
            type: assignment.assignment_type || 'other',
            allowResubmission: assignment.allow_resubmission || false
          };
          
          setFormData(fallbackFormData);
          setAssignmentData(assignment);
          logger.debug('Using fallback assignment data');
        } else {
          setErrors({ fetch: response.message || 'Failed to load assignment details' });
        }
      }
    } catch (error) {
      logger.error('Error fetching assignment details', { error, assignmentId: assignment?.id });
        // Fallback to using the assignment prop data
        if (assignment) {
          logger.debug('Falling back to assignment prop data due to error', { assignmentId: assignment.id });
        const chicagoDateTime = assignment.due_date ? parseUTCToChicago(assignment.due_date) : null;
        
        const fallbackFormData = {
          title: assignment.title || '',
          description: assignment.description 
            ? { content_json: null, content_html: assignment.description, content_plain: assignment.description.replace(/<[^>]*>/g, '') }
            : { content_json: null, content_html: '', content_plain: '' },
          points: assignment.points || 100,
          dueDate: chicagoDateTime ? chicagoDateTime.toFormat('yyyy-MM-dd') : '',
          dueTime: chicagoDateTime ? chicagoDateTime.toFormat('HH:mm') : '',
          type: assignment.assignment_type || 'other',
          allowResubmission: assignment.allow_resubmission || false
        };
        
        setFormData(fallbackFormData);
        setAssignmentData(assignment);
          logger.debug('Using fallback assignment data due to error');
      } else {
        setErrors({ fetch: error.message || 'Failed to load assignment details' });
      }
    } finally {
      setDataLoading(false);
    }
  }, [assignment]);

  // Fetch assignment details when modal opens
  useEffect(() => {
    if (assignment && isOpen) {
      fetchAssignmentDetails();
    }
  }, [assignment, isOpen, fetchAssignmentDetails]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const setPresetDateTime = (hoursFromNow) => {
    const now = getChicagoNow();
    const futureDateTime = now.plus({ hours: hoursFromNow });
    
    setFormData(prev => ({
      ...prev,
      dueDate: futureDateTime.toFormat('yyyy-MM-dd'),
      dueTime: '19:00' // Default to 7:00 PM
    }));
    
    // Clear any existing due date errors
    if (errors.dueDate) {
      setErrors(prev => ({
        ...prev,
        dueDate: ''
      }));
    }
  };

  const clearDueDate = () => {
    setFormData(prev => ({
      ...prev,
      dueDate: '',
      dueTime: ''
    }));
    
    // Clear any existing due date errors
    if (errors.dueDate) {
      setErrors(prev => ({
        ...prev,
        dueDate: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    // Validate description/instructions
    let hasDescription = false;
    if (formData.description) {
      if (typeof formData.description === 'object') {
        // Check for TipTap editor format (content_plain and content_html)
        if (formData.description.content_plain !== undefined) {
          const hasPlainText = formData.description.content_plain && formData.description.content_plain.trim() !== '';
          const hasHtml = formData.description.content_html && formData.description.content_html.trim() !== '' && formData.description.content_html !== '<p></p>';
          hasDescription = hasPlainText || hasHtml;
        } else if (formData.description.html !== undefined) {
          // Legacy format fallback
          hasDescription = formData.description.html && formData.description.html.trim() !== '' && formData.description.html !== '<p></p>';
        }
      } else if (typeof formData.description === 'string') {
        hasDescription = formData.description.trim() !== '' && formData.description !== '<p></p>';
      }
    }

    if (!hasDescription) {
      newErrors.description = 'Description is required';
    }

    if (formData.points < 1 || formData.points > 1000) {
      newErrors.points = 'Points must be between 1 and 1000';
    }

    // Validate due date and time if either is provided
    if (formData.dueDate || formData.dueTime) {
      if (!formData.dueDate) {
        newErrors.dueDate = 'Date is required when time is specified';
      } else if (!formData.dueTime) {
        newErrors.dueDate = 'Time is required when date is specified';
      } else {
        // Both date and time are provided, validate the combined datetime
        const dueDateTime = createChicagoDateTime(formData.dueDate, formData.dueTime);
        const now = getChicagoNow();

        if (!dueDateTime) {
          newErrors.dueDate = 'Invalid date or time format';
        } else if (dueDateTime < now) {
          newErrors.dueDate = 'Due date cannot be in the past';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Convert due date from separate date and time fields (Chicago time) to UTC
      let dueDateUTC = null;
      if (formData.dueDate && formData.dueTime) {
        const chicagoDateTime = createChicagoDateTime(formData.dueDate, formData.dueTime);
        
        if (chicagoDateTime) {
          dueDateUTC = chicagoDateTime.toUTC().toISO();
        }
      }

      // Extract description HTML from TipTap format
      let processedDescription = '';
      if (typeof formData.description === 'object') {
        // TipTap format: { content_json, content_html, content_plain }
        processedDescription = formData.description.content_html || formData.description.html || '';
      } else if (typeof formData.description === 'string') {
        processedDescription = formData.description;
      }

      // Build update data, only including optional fields if they have values
      const updateData = {
        title: formData.title,
        description: processedDescription,
        points: formData.points,
        type: formData.type,
        allowResubmission: formData.allowResubmission
      };

      // Only include dueDate if it has a value
      if (dueDateUTC) {
        updateData.dueDate = dueDateUTC;
      }

      await apiService.updateAssignment(assignment.id, updateData);
      onAssignmentUpdated();
      onClose();
      navigate('/assignments');
    } catch (error) {
      logger.error('Error updating assignment', { error, assignmentId: assignment?.id });
      setErrors({ submit: 'Failed to update assignment. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !assignment) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
        <ModalContent
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle>
              <FiBookOpen size={24} />
              Edit Assignment
            </ModalTitle>
            <CloseButton onClick={onClose}>
              <FiX size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            {dataLoading ? (
              <LoadingState>
                <FiClock size={24} />
                Loading assignment details...
              </LoadingState>
            ) : errors.fetch ? (
              <ErrorMessage>
                <FiAlertCircle size={20} />
                {errors.fetch}
              </ErrorMessage>
            ) : (
              <>
                <CourseInfo>
                  <CourseTitle>{assignmentData?.course_name || assignment?.course_name}</CourseTitle>
                  <CourseCode>{assignmentData?.course_code || assignment?.course_code}</CourseCode>
                </CourseInfo>

                {/* Current Description Display */}
                {(assignmentData?.description || assignment?.description) && (
                  <>
                    <CurrentDescriptionLabel>Current Description</CurrentDescriptionLabel>
                    <CurrentDescription
                      dangerouslySetInnerHTML={{ 
                        __html: sanitizeHtmlForViewer(assignmentData?.description || assignment?.description || 'No description provided') 
                      }}
                    />
                  </>
                )}

                <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>
                  Title <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter assignment title..."
                  error={!!errors.title}
                />
                {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>Description</Label>
                <RichTextEditorTipTap
                  value={formData.description}
                  onChange={(value) => handleInputChange('description', value)}
                  placeholder=""
                  error={!!errors.description}
                />
                {errors.description && <ErrorMessage>{errors.description}</ErrorMessage>}
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>
                    Points <Required>*</Required>
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max="1000"
                    value={formData.points}
                    onChange={(e) => handleInputChange('points', parseInt(e.target.value) || 0)}
                    placeholder="100"
                    error={!!errors.points}
                  />
                  {errors.points && <ErrorMessage>{errors.points}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>Assignment Type</Label>
                  <Select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                  >
                    <option value="essay">Essay</option>
                    <option value="exam">Exam</option>
                    <option value="discussion">Discussion</option>
                    <option value="project">Project</option>
                    <option value="other">Other</option>
                  </Select>
                </FormGroup>
              </FormRow>

              <FormGroup>
                <DateTimeContainer>
                  <DateTimeRow>
                    <DateTimeInput>
                      <DateTimeLabel>
                        <FiCalendar size={16} />
                        Due Date
                      </DateTimeLabel>
                      <DateTimeInputField
                        type="date"
                        value={formData.dueDate}
                        onChange={(e) => handleInputChange('dueDate', e.target.value)}
                        error={!!errors.dueDate}
                      />
                    </DateTimeInput>
                    
                    <DateTimeInput>
                      <DateTimeLabel>
                        <FiClock size={16} />
                        Due Time
                      </DateTimeLabel>
                      <DateTimeInputField
                        type="time"
                        value={formData.dueTime}
                        onChange={(e) => handleInputChange('dueTime', e.target.value)}
                        error={!!errors.dueDate}
                      />
                    </DateTimeInput>
                  </DateTimeRow>
                  
                  <PresetButtons>
                    <PresetButton type="button" onClick={(e) => { e.preventDefault(); setPresetDateTime(24); }}>
                      <FiZap size={14} />
                      Tomorrow
                    </PresetButton>
                    <PresetButton type="button" onClick={(e) => { e.preventDefault(); setPresetDateTime(72); }}>
                      <FiZap size={14} />
                      3 Days
                    </PresetButton>
                    <PresetButton type="button" onClick={(e) => { e.preventDefault(); setPresetDateTime(168); }}>
                      <FiZap size={14} />
                      1 Week
                    </PresetButton>
                    <PresetButton type="button" onClick={(e) => { e.preventDefault(); setPresetDateTime(336); }}>
                      <FiZap size={14} />
                      2 Weeks
                    </PresetButton>
                    <ClearButton type="button" onClick={(e) => { e.preventDefault(); clearDueDate(); }}>
                      No Due Date/Time
                    </ClearButton>
                  </PresetButtons>
                  
                  <TimezoneInfo>
                    <FiClock size={14} />
                    Leave date/time blank to allow submissions at any time. All times are in Chicago timezone.
                  </TimezoneInfo>
                </DateTimeContainer>
                {errors.dueDate && <ErrorMessage>{errors.dueDate}</ErrorMessage>}
              </FormGroup>

              {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}

              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  id="allowResubmission"
                  checked={formData.allowResubmission}
                  onChange={(e) => handleInputChange('allowResubmission', e.target.checked)}
                />
                <CheckboxLabel htmlFor="allowResubmission">
                  Allow students to resubmit this assignment after initial submission
                </CheckboxLabel>
              </CheckboxGroup>

              <ButtonGroup>
                <Button
                  type="button"
                  className="secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="primary"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update Assignment'}
                </Button>
              </ButtonGroup>
                </Form>
              </>
            )}
          </ModalBody>
        </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default EditAssignmentModal;
